import * as React from 'react';
import { css, styled } from 'styled-components';
import { Drawer } from '@mui/material';
import { MessageTypes } from '../../core/enums';
import {
	brandingColourHeavy,
	labelRedColour,
	superLightGreyColour,
	tabletLandscapeMinWidth,
	tabletPortraitMinWidth,
	whiteColour
} from '../../appComponents/cssConstants';
import ActionItem, { ModalAction } from './ActionItem';
import useSoftKeyboardHeightStore from '../../zustandStore/softKeyboardHeightStore';
import { LeftBlueOrWhiteIconForModal } from '../../appComponents/pages_v2/common/LeftBlueOrWhiteIcon';
import ImageContainer, { StyledImgContainer } from './ImageContainer';
import CloseButton from './CloseButton';
import ErrorSvg from '../../images/error.svg';

const BottomModal = ({
	imageSrc = null,
	imageComponent = null,
	title,
	body,
	actions = null,
	open,
	onClose,
	type = MessageTypes.Information,
	children,
	smallMinHeight = false, // For short modal with auto focus form input which will bring up soft keyboard
	fullScreenHeight = false,
	titleAlign,
	bodyAlign = 'left',
	imageSize,
	actionsMarginTop
}: Props) => {
	const hasImage = imageSrc || imageComponent;
	const softKeyboardHeight = useSoftKeyboardHeightStore((state) => state.softKeyboardHeight);

	return (
		<StyledDrawer
			anchor="bottom"
			open={open}
			onClose={onClose}
			$softKeyboardHeight={softKeyboardHeight}
			$smallMinHeight={smallMinHeight}
			$fullScreenHeight={fullScreenHeight}
		>
			<DrawerContainer>
				<Styledspan>
					<CloseButton onClick={onClose} />
				</Styledspan>

				{hasImage && (
					<StyledImgContainerForPhone>
						{imageComponent ? (
							<StyledImgContainer>{imageComponent}</StyledImgContainer>
						) : (
							<ImageContainer imageSrc={imageSrc} imageSize={imageSize} />
						)}
					</StyledImgContainerForPhone>
				)}

				<DrawerHeader $hasImage={!!hasImage || type === MessageTypes.Error} $titleAlign={titleAlign}>
					<LeftBlueOrWhiteIconForModal onClick={onClose} />
					<DrawerHeaderTitle $type={type} $titleAlign={titleAlign}>
						{title}
					</DrawerHeaderTitle>
				</DrawerHeader>

				<section>
					{type === MessageTypes.Error ? (
						<StyledImgContainerForTablet>
							<ImageContainer imageSrc={ErrorSvg} />
						</StyledImgContainerForTablet>
					) : (
						hasImage && (
							<StyledImgContainerForTablet>
								{imageComponent ? (
									<StyledImgContainer>{imageComponent}</StyledImgContainer>
								) : (
									<ImageContainer imageSrc={imageSrc} imageSize={imageSize} />
								)}
							</StyledImgContainerForTablet>
						)
					)}

					<BodyContainer>
						{body.map((paragraph) => (
							<BodyContent key={paragraph} $textAlign={bodyAlign}>
								{paragraph}
							</BodyContent>
						))}
					</BodyContainer>
					{children}
					{actions && (
						<ActionUl $marginTop={actionsMarginTop}>
							{actions.map((action) => (
								<ActionItem key={action.label} action={action} onClick={onClose} messageType={type} />
							))}
						</ActionUl>
					)}
				</section>
			</DrawerContainer>
		</StyledDrawer>
	);
};

export default BottomModal;

interface Props {
	imageSrc?: string | null;
	imageComponent?: React.ReactNode;
	title: string;
	body: string[];
	actions?: ModalAction[] | null;
	open: boolean;
	onClose: () => void;
	type?: MessageTypes;
	children?: React.ReactNode;
	smallMinHeight?: boolean;
	fullScreenHeight?: boolean;
	titleAlign?: string;
	bodyAlign?: string;
	imageSize?: number;
	actionsMarginTop?: number;
}

export const TabletPortraitStyles = css`
	top: 10vh;
	bottom: auto;
	left: auto;
	right: auto;
	height: auto;
	min-height: 30vh;
	max-height: 80vh;
	border-radius: 10px;
	width: 80%;
`;

export const DrawerPaperStyles = (
	smallMinHeight?: boolean,
	fullScreenHeight?: boolean,
	softKeyboardHeight?: number
) => css`
	border-radius: 12px 12px 0 0;
	min-height: ${smallMinHeight ? '20vh' : '50vh'};
	max-height: calc(${fullScreenHeight ? '90vh' : '70vh'} - ${softKeyboardHeight ?? 0}px);
	overflow-y: auto;
`;

export const DrawerHeaderTitleStyle = css`
	font-size: 14px;
	font-weight: 600;
	line-height: 24px;
	margin: 10px 0 15px 0;

	@media (min-width: ${tabletLandscapeMinWidth}) {
		font-size: 24px;
	}
`;

export const DrawerHeaderTabletPortraitStyle = css`
	text-align: center;
	font-size: 17px;
	font-style: normal;
	font-weight: 400;
	line-height: 22px;
	letter-spacing: -0.43px;
	margin: 0 auto;
	margin-top: 0px;
	padding: 11px 70px 11px 0; // Left arrow icon with 'Back' is 70px width, so add this padding to centre align header text
	width: 100%;
`;

export const DrawerHeaderStyle = css`
	display: flex;
	align-items: center;
	justify-content: space-between;

	@media (min-width: ${tabletPortraitMinWidth}) {
		padding-bottom: 11px;
		border-bottom: 0.5px solid #c6c3c3;
		margin-bottom: 28px;
	}
`;

const StyledDrawer = styled(Drawer)<{
	$softKeyboardHeight: number;
	$smallMinHeight?: boolean;
	$fullScreenHeight?: boolean;
}>`
	.MuiDrawer-paper {
		${(props) => DrawerPaperStyles(props.$smallMinHeight, props.$fullScreenHeight, props.$softKeyboardHeight)};
		background: ${whiteColour};
		padding: 16px 26px;
		bottom: ${(props) => `${props.$softKeyboardHeight}px`};
		max-width: 750px;

		@media (min-width: ${tabletPortraitMinWidth}) {
			${TabletPortraitStyles}
		}
	}
`;

const DrawerContainer = styled.div`
	margin-bottom: 87px;
`;

const DrawerHeader = styled.section<{ $hasImage: boolean; $titleAlign?: string }>`
	align-items: center;
	justify-content: ${(props) => (props.$titleAlign ? props.$titleAlign : 'center')};

	@media (min-width: ${tabletPortraitMinWidth}) {
		display: flex;
		justify-content: space-between;
		padding-bottom: 11px;
		border-bottom: 0.5px solid #c6c3c3;
		margin-bottom: 28px;
	}
`;

const DrawerHeaderTitle = styled.p<{ $type: MessageTypes; $titleAlign?: string }>`
	text-align: ${(props) => props.$titleAlign ?? 'left'};
	font-size: 22px;
	font-weight: 500;
	line-height: 28px;
	color: ${(props) => (props.$type === MessageTypes.Error ? labelRedColour : brandingColourHeavy)};

	@media (min-width: ${tabletPortraitMinWidth}) {
		font-size: 24px;
		position: absolute;
		left: 50%;
		transform: translateX(-50%);
	}
`;

const BodyContainer = styled.div`
	margin: 16px 0;
`;

const BodyContent = styled.p<{ $textAlign?: string }>`
	text-align: ${(props) => (props.$textAlign ? props.$textAlign : 'center')};
	font-size: 14px;
	line-height: 20px;
	letter-spacing: 0.25px;
	color: ${superLightGreyColour};
	word-break: break-word;

	@media (min-width: ${tabletPortraitMinWidth}) {
		font-size: 16px;
	}
`;

const ActionUl = styled.ul<{ $marginTop?: number }>`
	list-style: none;
	margin: ${(props) => props.$marginTop ?? 0}px 0 0 0;
	padding: 0;
	text-align: center;
	justify-content: center;
	display: flex;
	flex-direction: column;
	align-items: center;
`;

export const SpanStyled = css`
	display: block;
	cursor: pointer;

	@media (min-width: ${tabletPortraitMinWidth}) {
		display: none;
	}
`;

const Styledspan = styled.span`
	${SpanStyled}
`;

const StyledImgContainerForTablet = styled.div`
	display: none;

	@media (min-width: ${tabletPortraitMinWidth}) {
		display: block;
	}
`;

const StyledImgContainerForPhone = styled.div`
	display: block;

	@media (min-width: ${tabletPortraitMinWidth}) {
		display: none;
	}
`;
